// TaxInfoPanel — Feb 28 2026 v6 (Randy Compliance Pass 3)
//
// W-9 collection UI + 1099-NEC download surface. Lives in Account & Billing.
//
// UX shape:
//   • Idle state (no W-9 on file, YTD < $500): one-line status + optional
//     "File early" button. Zero friction for creators who'll never earn.
//   • Warning state (YTD >= $500 no W-9): coral banner "You're close to
//     the $600 IRS bar — file your W-9 now so payouts keep flowing."
//   • Filed state: green banner "W-9 on file — cash payouts unlocked",
//     shows masked TIN (last-4), download-signed-W-9 button.
//   • Historical 1099s: list of years available with download buttons.
//
// Security:
//   • TIN input shown but converted to masked once saved (backend never
//     returns cleartext outside of the PDF endpoint).
//   • Typed-signature audit trail: signer must retype their legal name.
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Receipt, Warning, CheckCircle, DownloadSimple, ArrowRight, LockKey } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const fmt$ = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC","PR",
];

export default function TaxInfoPanel({ testid = "tax-info-panel" }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/account/tax-info");
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load tax info.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return (
      <div className="bg-white border hairline rounded-xl p-5" data-testid={testid}>
        <p className="text-sm text-[#6B6A68]">Loading tax info…</p>
      </div>
    );
  }
  if (!data) return null;

  const { tax_info, current_year, current_year_ytd_cents, irs_threshold_cents, needs_w9, years_available } = data;
  const hasW9 = !!(tax_info && tax_info.verified);
  const nearingThreshold = current_year_ytd_cents >= irs_threshold_cents * 0.5 && !hasW9;

  const downloadW9 = async () => {
    try {
      const r = await api.get("/account/tax-info/w9/pdf", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-silo-w9-${tax_info?.legal_name?.replace(/\s+/g, "-").toLowerCase() || "user"}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't download W-9.");
    }
  };

  const download1099 = async (year) => {
    try {
      const r = await api.get(`/account/tax-info/1099/${year}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `my-silo-1099-nec-${year}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || `Couldn't download 1099 for ${year}.`);
    }
  };

  return (
    <div className="bg-white border hairline rounded-xl p-5 space-y-4" data-testid={testid}>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-md bg-[#F0F1EE] text-[#4A5D4E] flex items-center justify-center shrink-0">
          <Receipt size={20} weight="duotone" />
        </div>
        <div className="flex-1">
          <h3 className="font-serif-display text-xl text-[#1C1B1A]">Tax Info · W-9 & 1099-NEC</h3>
          <p className="text-sm text-[#6B6A68] mt-1">
            US federal law requires a Form 1099-NEC filing for anyone paid $600+ in a
            calendar year. File a W-9 before you cross the bar and your cash payouts keep
            flowing without interruption.
          </p>
        </div>
      </div>

      {/* YTD status line */}
      <div className="flex items-center justify-between text-sm text-[#6B6A68] pb-3 border-b border-[#E4E2DC]">
        <span>
          {current_year} year-to-date received:{" "}
          <strong className="text-[#1C1B1A] tabular-nums">
            {fmt$(current_year_ytd_cents)}
          </strong>
          {" "}of{" "}
          <span className="tabular-nums">{fmt$(irs_threshold_cents)}</span>
          {" "}reporting bar
        </span>
      </div>

      {/* State-driven main content */}
      {hasW9 ? (
        <div className="bg-[#EAF3EC] border border-[#B9D4BE] rounded-lg p-4" data-testid="tax-info-w9-filed">
          <div className="flex items-start gap-3">
            <CheckCircle size={22} weight="fill" className="text-[#4A5D4E] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-[#1C1B1A]">W-9 on file — cash payouts unlocked.</div>
              <div className="text-xs text-[#3A4D3E] mt-1">
                <strong>{tax_info.legal_name}</strong>
                {tax_info.business_name ? ` (${tax_info.business_name})` : ""}
                {" · "}
                {tax_info.tin_type} ending in {tax_info.tin_last4}
                {" · "}
                signed {new Date(tax_info.w9_signed_at).toLocaleDateString()}
              </div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <button
                  onClick={downloadW9}
                  data-testid="tax-info-download-w9"
                  className="text-xs px-3 py-1.5 rounded-md bg-white border hairline text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5"
                >
                  <DownloadSimple size={12} /> Download signed W-9
                </button>
                <button
                  onClick={() => setFormOpen(true)}
                  data-testid="tax-info-update-w9"
                  className="text-xs px-3 py-1.5 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]"
                >
                  Update info
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : nearingThreshold ? (
        <div className="bg-[#FDF3F1] border border-[#F1C5BE] rounded-lg p-4" data-testid="tax-info-nearing-threshold">
          <div className="flex items-start gap-3">
            <Warning size={22} weight="fill" className="text-[#C16E5A] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-[#1C1B1A]">
                {needs_w9 ? "You've crossed the reporting bar — file your W-9 now." : "You're getting close to the $600 IRS bar."}
              </div>
              <div className="text-xs text-[#6B6A68] mt-1">
                Once you cross <strong>{fmt$(irs_threshold_cents)}</strong> in a calendar year,
                further cash payouts are held until you file your W-9. Filing takes 30 seconds.
              </div>
              <button
                onClick={() => setFormOpen(true)}
                data-testid="tax-info-file-w9-warning"
                className="mt-3 px-4 py-2 rounded-md bg-[#1C1B1A] text-white text-sm hover:bg-[#3A4A3E] flex items-center gap-1.5"
              >
                File your W-9 <ArrowRight size={13} />
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="bg-[#F5F4F0] border border-[#E4E2DC] rounded-lg p-4" data-testid="tax-info-idle">
          <div className="flex items-start gap-3">
            <LockKey size={22} weight="duotone" className="text-[#6B6A68] shrink-0 mt-0.5" />
            <div className="flex-1">
              <div className="font-medium text-[#1C1B1A]">No W-9 on file.</div>
              <div className="text-xs text-[#6B6A68] mt-1">
                You don't need one yet — your year-to-date received is below the reporting bar. But
                if you'd like to file early to avoid any interruption later, you can.
              </div>
              <button
                onClick={() => setFormOpen(true)}
                data-testid="tax-info-file-w9-idle"
                className="mt-3 text-xs px-3 py-1.5 rounded-md bg-white border hairline text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E]"
              >
                File W-9 now (optional)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Historical 1099s */}
      {years_available && years_available.length > 0 && (
        <div className="pt-4 border-t border-[#E4E2DC]" data-testid="tax-info-1099-list">
          <div className="text-xs uppercase tracking-[0.2em] text-[#6B6A68] mb-3">
            Available 1099-NEC forms
          </div>
          <div className="space-y-2">
            {years_available.map((y) => (
              <div key={y.tax_year} className="flex items-center justify-between text-sm">
                <div>
                  <strong>{y.tax_year}</strong>
                  {" · "}
                  <span className="text-[#6B6A68]">{fmt$(y.total_paid_cents)} received</span>
                </div>
                <button
                  onClick={() => download1099(y.tax_year)}
                  data-testid={`tax-info-download-1099-${y.tax_year}`}
                  className="text-xs px-3 py-1.5 rounded-md bg-white border hairline text-[#1C1B1A] hover:border-[#4A5D4E] hover:text-[#4A5D4E] flex items-center gap-1.5"
                >
                  <DownloadSimple size={12} /> Copy B
                </button>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-[#9A9892] mt-3 leading-relaxed">
            Copy B is your recipient copy — keep it with your tax records. My Silo files Copy A
            with the IRS separately.
          </p>
        </div>
      )}

      {formOpen && (
        <W9FormModal
          existing={tax_info}
          onClose={() => setFormOpen(false)}
          onSaved={() => { setFormOpen(false); load(); toast.success("W-9 saved."); }}
        />
      )}
    </div>
  );
}

function W9FormModal({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    taxpayer_type: existing?.taxpayer_type || "individual",
    legal_name: existing?.legal_name || "",
    business_name: existing?.business_name || "",
    address_street: existing?.address?.street || "",
    address_city: existing?.address?.city || "",
    address_state: existing?.address?.state || "",
    address_zip: existing?.address?.zip || "",
    address_country: existing?.address?.country || "US",
    tin_type: existing?.tin_type || "SSN",
    tin: "",
    signature_name: "",
    certify: false,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.certify) return toast.error("You must certify under penalties of perjury.");
    if (form.signature_name.trim().toLowerCase() !== form.legal_name.trim().toLowerCase()) {
      return toast.error("Typed signature must exactly match your legal name.");
    }
    setSaving(true);
    try {
      const { certify, ...payload } = form;
      await api.post("/account/tax-info/w9", payload);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't save W-9.");
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[130] bg-black/50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose} data-testid="tax-info-w9-modal">
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl max-w-lg w-full my-8 shadow-2xl overflow-hidden"
      >
        <div className="p-5 border-b border-[#E4E2DC]">
          <h2 className="font-serif-display text-2xl text-[#1C1B1A]">Form W-9</h2>
          <p className="text-xs text-[#6B6A68] mt-1">
            Substitute W-9 for My Silo records. Info is encrypted at rest and never shared with third
            parties except for IRS filings you authorize.
          </p>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">Taxpayer type</label>
            <div className="flex gap-2">
              {["individual", "business"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, taxpayer_type: t })}
                  data-testid={`w9-taxpayer-type-${t}`}
                  className={`flex-1 px-4 py-2.5 rounded-md border text-sm capitalize ${form.taxpayer_type === t ? "bg-[#1C1B1A] text-white border-[#1C1B1A]" : "bg-white text-[#1C1B1A] border-[#E4E2DC]"}`}
                >
                  {t === "individual" ? "Individual / Sole Prop" : "Business Entity"}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">
              Legal name (as it appears on your tax return)
            </label>
            <input
              required
              value={form.legal_name}
              onChange={(e) => setForm({ ...form, legal_name: e.target.value })}
              data-testid="w9-legal-name"
              className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
          </div>

          {form.taxpayer_type === "business" && (
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">Business name (if different)</label>
              <input
                value={form.business_name}
                onChange={(e) => setForm({ ...form, business_name: e.target.value })}
                data-testid="w9-business-name"
                className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
            </div>
          )}

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">Street address</label>
            <input
              required
              value={form.address_street}
              onChange={(e) => setForm({ ...form, address_street: e.target.value })}
              data-testid="w9-address-street"
              className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
            />
          </div>

          <div className="grid grid-cols-3 gap-2">
            <div className="col-span-3 sm:col-span-1">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">City</label>
              <input
                required
                value={form.address_city}
                onChange={(e) => setForm({ ...form, address_city: e.target.value })}
                data-testid="w9-address-city"
                className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">State</label>
              <select
                required
                value={form.address_state}
                onChange={(e) => setForm({ ...form, address_state: e.target.value })}
                data-testid="w9-address-state"
                className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              >
                <option value="">—</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">ZIP</label>
              <input
                required
                value={form.address_zip}
                onChange={(e) => setForm({ ...form, address_zip: e.target.value })}
                data-testid="w9-address-zip"
                className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">TIN type</label>
            <div className="flex gap-2">
              {["SSN", "EIN"].map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setForm({ ...form, tin_type: t })}
                  data-testid={`w9-tin-type-${t}`}
                  className={`flex-1 px-4 py-2.5 rounded-md border text-sm ${form.tin_type === t ? "bg-[#1C1B1A] text-white border-[#1C1B1A]" : "bg-white text-[#1C1B1A] border-[#E4E2DC]"}`}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">
              {form.tin_type} ({form.tin_type === "SSN" ? "9 digits, e.g. 123-45-6789" : "9 digits, e.g. 12-3456789"})
            </label>
            <input
              required
              type="password"
              autoComplete="off"
              value={form.tin}
              onChange={(e) => setForm({ ...form, tin: e.target.value })}
              data-testid="w9-tin"
              className="w-full bg-white border hairline rounded-md px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              placeholder={form.tin_type === "SSN" ? "___-__-____" : "__-_______"}
            />
            <p className="text-[10px] text-[#9A9892] mt-1">
              Encrypted at rest. Only the last 4 digits are stored unmasked.
            </p>
          </div>

          <div className="pt-3 border-t border-[#E4E2DC]">
            <label className="flex items-start gap-2 text-xs text-[#6B6A68] cursor-pointer">
              <input
                type="checkbox"
                checked={form.certify}
                onChange={(e) => setForm({ ...form, certify: e.target.checked })}
                data-testid="w9-certify"
                className="mt-0.5"
              />
              <span>
                <strong>Under penalties of perjury,</strong> I certify that: (1) the TIN above is correct, (2) I am not
                subject to backup withholding, (3) I am a U.S. citizen or U.S. person, and (4) any FATCA
                code(s) on this form are correct.
              </span>
            </label>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-[#6B6A68] mb-1.5">
              Typed signature (must match legal name exactly)
            </label>
            <input
              required
              value={form.signature_name}
              onChange={(e) => setForm({ ...form, signature_name: e.target.value })}
              data-testid="w9-signature"
              className="w-full bg-white border hairline rounded-md px-3 py-3 text-base font-serif italic focus:outline-none focus:ring-1 focus:ring-[#4A5D4E]"
              placeholder="Sign here"
            />
          </div>
        </div>

        <div className="p-4 bg-[#F5F4F0] border-t border-[#E4E2DC] flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            data-testid="w9-cancel"
            className="text-sm px-4 py-2 rounded-md text-[#6B6A68] hover:text-[#1C1B1A]"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving || !form.certify}
            data-testid="w9-submit"
            className="text-sm px-5 py-2 rounded-md bg-[#1C1B1A] text-white hover:bg-[#3A4A3E] disabled:opacity-60 flex items-center gap-1.5"
          >
            {saving ? "Saving…" : <>File W-9 <ArrowRight size={13} /></>}
          </button>
        </div>
      </form>
    </div>
  );
}
